import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { File, Download, Loader2 } from 'lucide-react';
import { VoiceMessagePlayer } from './VoiceMessagePlayer';

interface AttachmentDisplayProps {
  attachmentUrl: string;
  getFileType: (url: string) => 'image' | 'video' | 'audio' | 'document';
  isOwnMessage?: boolean;
  senderLanguage?: string;
}

export const AttachmentDisplay = ({ 
  attachmentUrl, 
  getFileType, 
  isOwnMessage = false,
  senderLanguage 
}: AttachmentDisplayProps) => {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchSignedUrl = async () => {
      try {
        // Handle legacy public URLs (already full URLs)
        if (attachmentUrl.startsWith('http')) {
          setSignedUrl(attachmentUrl);
          setLoading(false);
          return;
        }

        // Handle new path format with attachment: prefix
        const path = attachmentUrl.replace('attachment:', '');
        
        const { data, error } = await supabase.storage
          .from('chat-attachments')
          .createSignedUrl(path, 3600); // 1 hour expiry

        if (error) {
          console.error('Error creating signed URL:', error);
          setError(true);
        } else {
          setSignedUrl(data.signedUrl);
        }
      } catch (err) {
        console.error('Error fetching signed URL:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchSignedUrl();
  }, [attachmentUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 bg-background/50 rounded-lg">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  if (error || !signedUrl) {
    return (
      <div className="flex items-center gap-2 p-2 bg-background/50 rounded text-muted-foreground">
        <File className="h-4 w-4" />
        <span className="text-sm">Unable to load attachment</span>
      </div>
    );
  }

  const fileType = getFileType(attachmentUrl);

  if (fileType === 'image') {
    return (
      <img 
        src={signedUrl} 
        alt="Attachment" 
        className="rounded-lg max-w-full max-h-64 object-cover cursor-pointer"
        onClick={() => window.open(signedUrl, '_blank')}
      />
    );
  }

  if (fileType === 'video') {
    return (
      <video 
        src={signedUrl} 
        controls 
        className="rounded-lg max-w-full max-h-64"
      />
    );
  }

  if (fileType === 'audio') {
    return (
      <VoiceMessagePlayer 
        audioUrl={signedUrl} 
        isOwnMessage={isOwnMessage}
        senderLanguage={senderLanguage}
      />
    );
  }

  // Document
  return (
    <a 
      href={signedUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-2 p-2 bg-background/50 rounded hover:bg-background/70 transition-colors"
    >
      <File className="h-4 w-4" />
      <span className="text-sm">Download file</span>
      <Download className="h-3 w-3 ml-auto" />
    </a>
  );
};
